import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  FormField,
  Grid,
  Heading,
  IDialogProps,
  InputGroup,
  majorScale,
  TextArea,
  useToast,
} from '../../npaper';
import { useStores } from '../../stores';
import { ILocation } from '../../stores/warehousing/LocationStore';
import { IWarehouse } from '../../stores/warehousing/WarehouseStore';
import WarehouseSelect from './WarehouseSelect';

export interface ILocationDetailsDialogProps
  extends Omit<IDialogProps, 'title' | 'actions' | 'onClose'> {
  location?: ILocation;
  warehouse?: IWarehouse;
  onClose?: (createdLocation?: ILocation) => void;
}

interface FormValues {
  code: string;
  description: string | null;
  warehouse: number | string;
}

const LocationDetailsDialog: React.FC<ILocationDetailsDialogProps> = ({
  location,
  warehouse,
  onClose,
  isShown: defaultIsShown,
  ...dialogProps
}) => {
  const [isShown, setIsShown] = useState(defaultIsShown);
  const intl = useIntl();
  const toaster = useToast();

  const { locationStore } = useStores();

  const { reset, control, formState, handleSubmit, errors, register } =
    useForm<FormValues>();

  useEffect(() => {
    if (location) {
      const snapshot = getSnapshot(location);
      reset({
        code: snapshot.code,
        description: snapshot.description,
      });
    } else {
      reset({
        warehouse: warehouse ? warehouse.id : undefined,
      });
    }
  }, [location, reset, warehouse]);

  const onSubmit = async (values: FormValues) => {
    if (location) {
      // update location
      try {
        await locationStore.updateItem(location, values);
        close();
      } catch (err) {
        toaster.showDefaultError(err);
      }
    } else {
      // Create the location
      try {
        const newLocation = await locationStore.createItem({
          ...values,
        });
        close(newLocation);
      } catch (err) {
        toaster.showDefaultError(err);
      }
    }
  };

  useEffect(() => {
    setIsShown(defaultIsShown);
  }, [defaultIsShown]);

  const close = (createdLocation?: ILocation) => {
    setIsShown(false);
    onClose && onClose(createdLocation);
  };

  return (
    <Dialog
      {...dialogProps}
      title={
        location
          ? intl.formatMessage({ defaultMessage: 'Edit location' })
          : intl.formatMessage({ defaultMessage: 'Create new location' })
      }
      isShown={isShown}
      onClose={() => {
        close();
      }}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {location ? (
              <FormattedMessage defaultMessage="Save changes" />
            ) : (
              <FormattedMessage defaultMessage="Create location" />
            )}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="Details" />
        </Heading>

        {!location && (
          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Warehouse' })}
            inline
            isInvalid={!!errors.warehouse}
            hint={errors.warehouse?.message}
          >
            <Controller
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Please select a warehouse',
                }),
              }}
              control={control}
              name="warehouse"
              defaultValue={warehouse ? warehouse.id : undefined}
              render={({ onChange, onBlur, value }) => (
                <WarehouseSelect
                  onOpen={onBlur}
                  onSelect={(warehouse: IWarehouse) => onChange(warehouse.id)}
                  value={value}
                />
              )}
            />
          </FormField>
        )}

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Code' })}
          inline
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Location code',
            })}
            isInvalid={!!errors.code}
            validationMessage={errors.code && errors.code.message}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please provide a location code, e.g. A01',
              }),
              maxLength: {
                value: 128,
                message: intl.formatMessage({
                  defaultMessage:
                    'Location codes must be 128 characters or less',
                }),
              },
            })}
            name="code"
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Description' })}
          inline
        >
          <TextArea
            placeholder={intl.formatMessage({
              defaultMessage: 'Location description',
            })}
            isInvalid={!!errors.description}
            validationMessage={errors.description && errors.description.message}
            ref={register({
              maxLength: {
                value: 300,
                message: intl.formatMessage({
                  defaultMessage: 'Description must be 300 characters or less',
                }),
              },
            })}
            name="description"
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};

export default LocationDetailsDialog;
